import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bus, Calendar, Wrench, AlertTriangle,
  CheckCircle, MapPin, Gauge, User, Phone, DollarSign,
  Clock, Settings, FileText, TrendingUp, CreditCard
} from 'lucide-react';
import { busesApi, mantenimientosApi, mecanicosApi } from '../lib/api';
import { toast } from 'sonner';
import PagarCreditoModal from '../components/PagarCreditoModal';
import DeudaAntiguaModal from '../components/DeudaAntiguaModal';

interface BusType {
  id: string;
  padron?: string;
  numero?: string;
  placa: string;
  marca: string;
  modelo: string;
  año?: number;
  kilometraje_actual: number;
  kilometraje?: number;
  capacidad?: number;
  estado: string;
  fecha_compra?: string;
  conductor_principal?: string;
  ultimo_mantenimiento?: string;
  combustible: string;
  chasis: string;
  motor: string;
  fecha_creacion?: string;
  proximo_servicio_km?: number;
  servicios_completados?: number;
  ranking_score?: number;
  dueno?: string;
  telefono?: string;
  credito_usado?: number;
  credito_usado_productos?: number;
  credito_usado_servicios?: number;
}

interface MantenimientoType {
  id: string;
  bus_id: string;
  tipo: string;
  titulo?: string;
  descripcion: string;
  fecha?: string;
  fecha_programada?: string;
  fecha_completado?: string;
  kilometraje?: number;
  costo?: number;
  costo_estimado?: number;
  costo_real?: number;
  mecanico_id?: string;
  mecanicos?: string;
  mecanico_personalizado?: string;
  estado: string;
  prioridad?: string;
}

interface BusDetalleProps {
  busIdProp?: string;
  onClose?: () => void;
}

const BusDetalle = ({ busIdProp, onClose }: BusDetalleProps = {}) => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = busIdProp || idParam;
  const navigate = useNavigate();
  const volver = onClose || (() => navigate('/buses'));
  const [bus, setBus] = useState<BusType | null>(null);
  const [mantenimientos, setMantenimientos] = useState<MantenimientoType[]>([]);
  const [mecanicos, setMecanicos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [pestanaActiva, setPestanaActiva] = useState<'info' | 'mantenimientos' | 'estadisticas'>('info');
  const [mostrarReporte, setMostrarReporte] = useState(false);
  const [generandoReporte, setGenerandoReporte] = useState(false);
  const [fechaInicioReporte, setFechaInicioReporte] = useState('');
  const [fechaFinReporte, setFechaFinReporte] = useState('');
  const [mostrarPago, setMostrarPago] = useState(false);
  const [mostrarDeudaAntigua, setMostrarDeudaAntigua] = useState(false);

  useEffect(() => {
    cargarDatosBus();
  }, [id]);

  const cargarDatosBus = async () => {
    try {
      setCargando(true);

      const busData = await busesApi.getById(id!);
      setBus(busData);

      const [todosMantenimientos, todosMecanicos] = await Promise.all([
        mantenimientosApi.getAll(),
        mecanicosApi.getAll()
      ]);
      setMecanicos(todosMecanicos || []);
      const mantData = (todosMantenimientos || [])
        .filter((m: any) => m.bus_id === id)
        .sort((a: any, b: any) => {
          const fechaA = a.fecha_programada ? new Date(a.fecha_programada).getTime() : 0;
          const fechaB = b.fecha_programada ? new Date(b.fecha_programada).getTime() : 0;
          return fechaB - fechaA;
        });
      setMantenimientos(mantData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  // Mismo criterio que MantenimientosCompleto.tsx: "mecanicos" puede ser un texto (mecanico
  // externo) o varios ids separados por coma; si no viene, cae a mecanico_id o mecanico_personalizado.
  const getMecanicosNombres = (mant: MantenimientoType) => {
    if (mant.mecanicos) {
      if (!mant.mecanicos.includes(',') && !mant.mecanicos.match(/^[0-9a-f-]+$/)) {
        return mant.mecanicos;
      }
      const nombres = mant.mecanicos.split(',').map((mecId: string) => {
        const mec = mecanicos.find(m => m.id === mecId.trim());
        return mec ? mec.nombre : mecId;
      }).filter(Boolean);
      if (nombres.length > 0) return nombres.join(', ');
    }
    if (mant.mecanico_id) {
      const mec = mecanicos.find(m => m.id === mant.mecanico_id);
      if (mec) return mec.nombre;
    }
    return mant.mecanico_personalizado || null;
  };

  const obtenerColorEstado = (estado: string) => {
    switch ((estado || '').toLowerCase()) {
      case 'activo':
        return 'bg-green-100 text-green-800';
      case 'mantenimiento':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactivo':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const obtenerColorPrioridad = (prioridad: string) => {
    switch (prioridad?.toLowerCase()) {
      case 'alta':
        return 'bg-red-100 text-red-800';
      case 'media':
        return 'bg-yellow-100 text-yellow-800';
      case 'baja':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const descargarReportePDF = async (tipo: 'general' | 'taller' = 'general') => {
    setGenerandoReporte(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const endpoint = tipo === 'taller'
        ? `${API_URL}/api/reportes/mantenimientos/pdf`
        : `${API_URL}/api/reportes/bus/${id}/pdf`;
      const body = tipo === 'taller'
        ? { bus_id: id, fecha_inicio: fechaInicioReporte || undefined, fecha_fin: fechaFinReporte || undefined }
        : { fechaInicio: fechaInicioReporte || undefined, fechaFin: fechaFinReporte || undefined };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('No se pudo generar el reporte');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = tipo === 'taller'
        ? `reporte_mantenimiento_taller_${bus?.placa || id}.pdf`
        : `reporte_bus_${bus?.placa || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Reporte generado');
      setMostrarReporte(false);
    } catch (error) {
      console.error('Error generando reporte:', error);
      toast.error('Error al generar el reporte');
    } finally {
      setGenerandoReporte(false);
    }
  };

  const calcularEstadisticas = () => {
    const completados = mantenimientos.filter(m => m.estado === 'completado').length;
    const pendientes = mantenimientos.filter(m => m.estado === 'pendiente').length;
    const costoTotal = mantenimientos
      .filter(m => m.estado === 'completado')
      .reduce((acc, m) => acc + (m.costo_real || m.costo || 0), 0);
    
    return { completados, pendientes, costoTotal };
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!bus) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Bus no encontrado</h3>
          <button
            onClick={volver}
            className="text-blue-600 hover:text-blue-800"
          >
            Volver a la lista de buses
          </button>
        </div>
      </div>
    );
  }

  const stats = calcularEstadisticas();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={volver}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {onClose ? 'Cerrar' : 'Volver a Buses'}
        </button>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-4 rounded-lg">
                <Bus className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Bus {bus.numero || bus.placa}
                </h1>
                <p className="text-gray-600 mt-1">
                  {bus.marca} {bus.modelo}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMostrarReporte(true)}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generar Reporte PDF
              </button>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${obtenerColorEstado(bus.estado)}`}>
                {bus.estado}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de reporte PDF */}
      {mostrarReporte && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Generar Reporte PDF
            </h2>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="font-medium">Bus: {bus.placa}</p>
              <p className="text-sm text-gray-600">{bus.marca} {bus.modelo}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Fecha de inicio (opcional)</label>
                <input
                  type="date"
                  value={fechaInicioReporte}
                  onChange={(e) => setFechaInicioReporte(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fecha de fin (opcional)</label>
                <input
                  type="date"
                  value={fechaFinReporte}
                  onChange={(e) => setFechaFinReporte(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Dejar ambas vacías para incluir todo el historial</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 space-y-1">
                <p>📋 "Descargar PDF" (reporte general) incluye:</p>
                <p>✅ Información completa del bus</p>
                <p>✅ Historial de mantenimientos y su costo</p>
                <p>✅ Servicios y ventas asociadas</p>
                <p className="pt-1">🔧 "Reporte para el Taller" es el formato FleetMantto: tabla de mantenimientos por unidad, lista para imprimir.</p>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => descargarReportePDF('taller')}
                disabled={generandoReporte}
                className="flex-1 px-4 py-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 text-sm"
              >
                {generandoReporte ? 'Generando...' : 'Reporte para el Taller'}
              </button>
            </div>

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setMostrarReporte(false)}
                disabled={generandoReporte}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => descargarReportePDF('general')}
                disabled={generandoReporte}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {generandoReporte ? 'Generando...' : 'Descargar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pestañas */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setPestanaActiva('info')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                pestanaActiva === 'info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Información General
            </button>
            <button
              onClick={() => setPestanaActiva('mantenimientos')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                pestanaActiva === 'mantenimientos'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Wrench className="h-4 w-4 inline mr-2" />
              Mantenimientos ({mantenimientos.length})
            </button>
            <button
              onClick={() => setPestanaActiva('estadisticas')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                pestanaActiva === 'estadisticas'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Estadísticas
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Información General */}
          {pestanaActiva === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos del Vehículo</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Placa</p>
                      <p className="font-medium">{bus.placa}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Bus className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Marca y Modelo</p>
                      <p className="font-medium">{bus.marca} {bus.modelo}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Año</p>
                      <p className="font-medium">{bus.año || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Gauge className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Kilometraje Actual</p>
                      <p className="font-medium">{bus.kilometraje_actual?.toLocaleString()} km</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Especificaciones</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Settings className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Motor</p>
                      <p className="font-medium">{bus.motor || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Settings className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Chasis</p>
                      <p className="font-medium">{bus.chasis || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Settings className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Combustible</p>
                      <p className="font-medium">{bus.combustible}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Capacidad</p>
                      <p className="font-medium">{bus.capacidad || 'No especificada'} pasajeros</p>
                    </div>
                  </div>
                </div>
              </div>

              {(bus.dueno || bus.telefono || bus.conductor_principal) && (
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Información de Contacto</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {bus.dueno && (
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Dueño</p>
                          <p className="font-medium">{bus.dueno}</p>
                        </div>
                      </div>
                    )}
                    {bus.telefono && (
                      <div className="flex items-center">
                        <Phone className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Teléfono</p>
                          <p className="font-medium">{bus.telefono}</p>
                        </div>
                      </div>
                    )}
                    {bus.conductor_principal && (
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Conductor Principal</p>
                          <p className="font-medium">{bus.conductor_principal}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Deuda a Crédito</h3>
                <div className={`rounded-lg p-4 flex items-center justify-between flex-wrap gap-3 ${
                  (bus.credito_usado || 0) > 0 ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center">
                    <CreditCard className={`h-6 w-6 mr-3 ${(bus.credito_usado || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="text-sm text-gray-500">Deuda actual del bus</p>
                      <p className={`text-xl font-bold ${(bus.credito_usado || 0) > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        S/ {(bus.credito_usado || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(bus.credito_usado || 0) > 0 && (
                      <button
                        onClick={() => setMostrarPago(true)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                      >
                        Pagar deuda
                      </button>
                    )}
                    <button
                      onClick={() => setMostrarDeudaAntigua(true)}
                      className="px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg text-sm font-semibold"
                    >
                      Agregar deuda antigua
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mantenimientos */}
          {pestanaActiva === 'mantenimientos' && (
            <div>
              {mantenimientos.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No hay mantenimientos registrados para este bus</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mantenimientos.map((mant) => (
                    <div key={mant.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900">
                              {mant.titulo || mant.tipo}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              mant.estado === 'completado' 
                                ? 'bg-green-100 text-green-800'
                                : mant.estado === 'en_proceso'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {mant.estado}
                            </span>
                            {mant.prioridad && (
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${obtenerColorPrioridad(mant.prioridad)}`}>
                                {mant.prioridad}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{mant.descripcion}</p>
                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            {(mant.fecha || mant.fecha_programada) && (
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {new Date(mant.fecha || mant.fecha_programada!).toLocaleDateString('es-PE')}
                              </div>
                            )}
                            {mant.kilometraje && (
                              <div className="flex items-center">
                                <Gauge className="h-4 w-4 mr-1" />
                                {mant.kilometraje.toLocaleString()} km
                              </div>
                            )}
                            {getMecanicosNombres(mant) && (
                              <div className="flex items-center">
                                <User className="h-4 w-4 mr-1" />
                                {getMecanicosNombres(mant)}
                              </div>
                            )}
                            {(mant.costo || mant.costo_real || mant.costo_estimado) && (
                              <div className="flex items-center">
                                <DollarSign className="h-4 w-4 mr-1" />
                                S/ {(mant.costo_real || mant.costo || mant.costo_estimado)?.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Estadísticas */}
          {pestanaActiva === 'estadisticas' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Mantenimientos Completados</p>
                      <p className="text-3xl font-bold text-green-600">{stats.completados}</p>
                    </div>
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Mantenimientos Pendientes</p>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pendientes}</p>
                    </div>
                    <Clock className="h-12 w-12 text-yellow-600" />
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Costo Total</p>
                      <p className="text-3xl font-bold text-blue-600">S/ {stats.costoTotal.toLocaleString()}</p>
                    </div>
                    <DollarSign className="h-12 w-12 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen del Vehículo</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total de Mantenimientos:</span>
                    <span className="font-semibold">{mantenimientos.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kilometraje Actual:</span>
                    <span className="font-semibold">{bus.kilometraje_actual?.toLocaleString()} km</span>
                  </div>
                  {bus.proximo_servicio_km && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Próximo Servicio:</span>
                      <span className="font-semibold">{bus.proximo_servicio_km.toLocaleString()} km</span>
                    </div>
                  )}
                  {bus.ultimo_mantenimiento && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Último Mantenimiento:</span>
                      <span className="font-semibold">{new Date(bus.ultimo_mantenimiento).toLocaleDateString('es-PE')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {mostrarPago && bus && (
        <PagarCreditoModal
          bus={bus}
          onClose={() => setMostrarPago(false)}
          onPagado={(_padron, nuevoSaldo, nuevoSaldoProductos, nuevoSaldoServicios) => setBus(prev => prev ? { ...prev, credito_usado: nuevoSaldo, credito_usado_productos: nuevoSaldoProductos, credito_usado_servicios: nuevoSaldoServicios } : prev)}
        />
      )}

      {mostrarDeudaAntigua && bus && (
        <DeudaAntiguaModal
          bus={bus}
          onClose={() => setMostrarDeudaAntigua(false)}
          onAgregada={(_padron, nuevoSaldo, nuevoSaldoProductos, nuevoSaldoServicios) => setBus(prev => prev ? { ...prev, credito_usado: nuevoSaldo, credito_usado_productos: nuevoSaldoProductos, credito_usado_servicios: nuevoSaldoServicios } : prev)}
        />
      )}
    </div>
  );
};

export default BusDetalle;
